import { useAuth, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useValidatePageAccess from "src/hooks/useValidatePageAccess";
import { createUserAPI, getUserMetadata, mapAnonymousThreads } from "src/store/slices/onboardingSlice";
import { handleAuthActionType, identifyAnalytics, trackAnalytics } from "../helpers";
import { Warning } from "@phosphor-icons/react";
import usePageTracking from "src/hooks/usePageTracking";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { resolveAnonymousThreadIds } from "src/utils/anonymousThreadId";
import { getClerkUserEmail, isClerkSignupSession } from "src/utils/clerkUserIdentity";
import { fetchMiscMetadata } from "src/store/slices/profileMemorySlice";
import { setFreeCallNudgeModal } from "src/store/slices/modalSlice";
import { CHECKOUT_ENTRY, CHECKOUT_ENTRY_STORAGE_KEY } from "src/store/slices/checkoutFlowSlice";

const PostAuth = () => {
  // post auth option 1 vs option 2
  // option 1: the first call goes to the backend to create the user. If the user is already created, then metadata is checked
  // option 2: we get metadata first. if response is 401, user is not created. call createUserAPI.
  // if response is 200, user is created. check if user has parties. if yes, redirect to homepage. if no, redirect to onboarding.
  useValidatePageAccess();
  const { user, isSignedIn } = useUser();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);
  const [createUserAttemptCount, setCreateUserAttemptCount] = useState(0);
  const [accessTokenValue, setAccessTokenValue] = useState(null);
  const [message, setMessage] = useState(
    `We're experiencing a temporary issue loading data. Please refresh the app to try again. `,
  );

  const email = getClerkUserEmail(user);
  const userMetadataFetchStatus = useSelector((state) => state.onboarding.userMetadataFetchStatus);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  // const userIdentified = useSelector((state) => state.onboarding.userIdentified);

  const { getToken, isLoaded } = useAuth();

  const getMyToken = async () => {
    const token = await getToken({ template: "Backend" });
    return token;
  };
  const getTokenValue = async () => {
    try {
      const token = await getMyToken();
      if (token && typeof token === "string") {
        localStorage.setItem("accessToken2", token);
        setAccessTokenValue(token);
      } else {
        // Log error if token is invalid
        ErrorLoggingV2(
          {
            error: "Invalid token received",
            tokenType: typeof token,
            tokenValue: token ? "present" : "null",
            user: user?.primaryEmailAddress?.emailAddress,
            timestamp: new Date().toISOString(),
          },
          "post_auth_invalid_token",
        );
      }
    } catch (error) {
      // Log error if token fetch fails
      ErrorLoggingV2(
        {
          error: error,
          message: error?.message,
          user: user?.primaryEmailAddress?.emailAddress,
          timestamp: new Date().toISOString(),
        },
        "post_auth_token_fetch_error",
      );
      setMessage("We are unable to authenticate you. Please try refreshing the page.");
    }
  };

  useEffect(() => {
    // Users without a name in Clerk still need identifying, or their email
    // never reaches PostHog.
    if (isSignedIn && email) {
      identifyAnalytics(email, userMetadata, dispatch);
    }
  }, [isSignedIn, email, userMetadata, dispatch]);

  useEffect(() => {
    // The signup session is reported as Completed Signup by useClerkEventTracking.
    if (isSignedIn && user && !isClerkSignupSession(user)) {
      // Event 72:
      trackAnalytics(EVENT_NAMES.COMPLETED_SIGNIN, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.USER_ID]: user?.id,
        [EVENT_PARAMETERS.USER_EMAIL]: email || "",
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        timestamp: new Date().toISOString(),
      });
    }
  }, [isSignedIn, userMetadata, dispatch, user, email]);

  useEffect(() => {
    if (!isLoaded) return;

    if (accessTokenValue) {
      var thread_id = queryParams.get("thread_id");
      // Map anonymous threads only after metadata confirms user exists on backend.
      // Calling map-anonymous before that returns UserNotFoundException.
      dispatch(getUserMetadata({ thread_id: thread_id })).then((result) => {
        if (result?.meta?.requestStatus === "fulfilled") {
          const anonymousThreadIds = resolveAnonymousThreadIds({ explicitId: thread_id });
          if (anonymousThreadIds.length > 0) {
            dispatch(mapAnonymousThreads({ threadIds: anonymousThreadIds }));
          }
          // Free onboarding call nudge — show a few seconds after signin if eligible.
          dispatch(fetchMiscMetadata()).then((miscResult) => {
            const nudgeValue = miscResult?.payload?.free_call_nudge_show;
            if (nudgeValue != null) {
              setTimeout(() => {
                dispatch(setFreeCallNudgeModal({ show: true }));
              }, 1500);
            }
          });
        }
      });
    } else {
      getTokenValue();
    }
  }, [accessTokenValue, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  usePageTracking();

  useEffect(() => {
    // if the profile is not created (status = 401), then create the user
    // if the profile is created but profile is not completed(parties not created), (status = 404), then redirect to onboarding
    // if the profile is created and profile is completed (status = 200), then fetch parties information, and rest will be taken care of next useEffect
    var thread_id = queryParams.get("thread_id");

    if (userMetadataFetchStatus === "success") {
      // Track successful post-auth flow completion

      // Event 73:
      trackAnalytics(EVENT_NAMES.POSTAUTH_FLOW_COMPLETE, {
        [EVENT_PARAMETERS.USER_ID]: user?.id,
        [EVENT_PARAMETERS.USER_EMAIL]: email || "",
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      });

      // If a redirect_url was passed (e.g. from shared link auth), go there directly
      var redirectUrl = queryParams.get("redirect_url");
      if (redirectUrl) {
        navigate(redirectUrl);
        return;
      }

      // Simple redirect: if thread_id exists, go to chat, otherwise go to home
      var authActionType = queryParams.get("action_type");
      var checkoutTier = queryParams.get("checkout_tier");
      // Checkout entry from the incoming URL (e.g. "get_started" from the marketing site); pricing_nav when absent/unknown.
      var requestedEntry = queryParams.get("entry");
      try {
        // Fallback for when the auth redirect dropped the query string (stashed by Signup/Signin).
        if (!requestedEntry) requestedEntry = sessionStorage.getItem(CHECKOUT_ENTRY_STORAGE_KEY);
        sessionStorage.removeItem(CHECKOUT_ENTRY_STORAGE_KEY);
      } catch {}
      var checkoutEntry = Object.values(CHECKOUT_ENTRY).includes(requestedEntry) ? requestedEntry : CHECKOUT_ENTRY.PRICING_NAV;
      if (authActionType !== "access_document" && authActionType !== "attachment_limit") {
        handleAuthActionType(authActionType, dispatch, userMetadata, null, email, navigate);
      }
      if (thread_id && thread_id !== "null") {
        let chatUrl;
        if (authActionType === "access_document") {
          const docId = localStorage.getItem("post_auth_doc_id");
          const docVersion = localStorage.getItem("post_auth_doc_version");
          const docTitle = localStorage.getItem("post_auth_doc_title");
          if (docId && docVersion) {
            chatUrl = `/chat/${thread_id}?view_doc=true&doc_id=${docId}&version=${docVersion}`;
            if (docTitle) {
              chatUrl += `&doc_title=${encodeURIComponent(docTitle)}`;
            }
          } else {
            chatUrl = `/chat/${thread_id}`;
          }
        } else {
          chatUrl = authActionType ? `/chat/${thread_id}?action_type=${authActionType}` : `/chat/${thread_id}`;
        }
        if (checkoutTier) {
          const tierRank = {
            free_tier: 0,
            pre_onboarding: 0,
            subscriber_business: 1,
            subscriber_enterprise: 2,
            inhouse_counsel: 3,
          };
          const currentRank = tierRank[userMetadata?.subscription_type] ?? 0;
          const requestedRank = tierRank[checkoutTier] ?? 0;
          if (currentRank < requestedRank) {
            const separator = chatUrl.includes("?") ? "&" : "?";
            chatUrl = `${chatUrl}${separator}modal=checkout&entry=${checkoutEntry}&step=checkout&tier=${checkoutTier}`;
          }
        }
        navigate(chatUrl);
      } else if (checkoutTier) {
        const tierRank = {
          free_tier: 0,
          pre_onboarding: 0,
          subscriber_business: 1,
          subscriber_enterprise: 2,
          inhouse_counsel: 3,
        };
        const currentRank = tierRank[userMetadata?.subscription_type] ?? 0;
        const requestedRank = tierRank[checkoutTier] ?? 0;
        if (currentRank >= requestedRank) {
          navigate("/home");
        } else {
          navigate(`/home?modal=checkout&entry=${checkoutEntry}&step=checkout&tier=${checkoutTier}`);
        }
      } else {
        const homeUrl = authActionType ? `/home?action_type=${authActionType}` : `/home`;
        navigate(homeUrl);
      }

      // Clear action_type and any doc-open intent from localStorage after using them.
      // post_auth_doc_id/version are only used by the access_document flow here — for
      // consult_attorney/finalize_document, handleAuthActionType on the chat page owns that cleanup.
      if (authActionType) {
        localStorage.removeItem("action_type");
        if (authActionType === "access_document") {
          localStorage.removeItem("post_auth_doc_id");
          localStorage.removeItem("post_auth_doc_version");
          localStorage.removeItem("post_auth_doc_title");
        }
      }
    }

    if (
      userMetadataFetchStatus !== "success" &&
      userMetadataFetchStatus !== "loading" &&
      userMetadataFetchStatus !== "idle"
    ) {
      switch (userMetadataFetchStatus) {
        case "token-failure":
          setMessage("We are unable to log you in. Please try again.");
          break;
        case "profile-not-completed":
          setMessage(
            <>
              Your Profile information isn't complete. Update your profile here <br />
              <a href="/onboarding" className="underline decoration-solid text-black text-center">
                Update Profile
              </a>
            </>,
          );
          break;
        default:
          setMessage(<>We're experiencing a temporary issue loading data. Please refresh the app to try again. </>);
      }
    }
    // else if (userMetadataFetchStatus === "success") {
    //     dispatch(getSavedParties());
    // }
  }, [userMetadataFetchStatus]); // eslint-disable-line

  return (
    <>
      {userMetadataFetchStatus !== "success" &&
        userMetadataFetchStatus !== "loading" &&
        userMetadataFetchStatus !== "idle" ? (
        <div className="w-full h-screen flex justify-center items-center flex-col gap-[8px]">
          <div className="md:w-[458px] w-full rounded-[8px] border px-[16px] py-[15px] flex gap-[8px] justify-center items-start bg-[#FFF6F5] ">
            <span className="w-[24px] h-[24px] my-auto">
              <Warning size={24} className="text-red-700" />
            </span>
            <p className="font-sans text-[16px] font-medium leading-[25px]">{message}</p>
          </div>
          <p className="font-sans font-medium text-[14px] leading-[22px] text-[#333333]">
            Reach out to the{" "}
            <a
              href="mailto:support@inhouse.ai"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-solid text-black"
            >
              support team
            </a>{" "}
            if the problem persists
          </p>
          {userMetadataFetchStatus !== "token-failure" ? (
            <p className="mx-auto">
              <a href="/home" className="underline">
                Home
              </a>
            </p>
          ) : (
            <p className="mx-auto">
              <p
                onClick={() => {
                  window.location.reload();
                }}
                className="underline cursor-pointer"
              >
                Refresh and try again
              </p>
            </p>
          )}
        </div>
      ) : (
        <div className="w-screen h-screen flex flex-col justify-center justify-items-center gap-[10px]">
          {/* <div className="flex flex-"> */}
          {createUserAttemptCount >= 5 ? (
            <>
              <p className="mx-auto">We are facing some issues.</p>
              <button
                className="btn-base btn-secondary flex justify-center w-auto !py-3 text-[14px] md:text-[16px]"
                onClick={() => {
                  dispatch(createUserAPI());
                  setCreateUserAttemptCount(0);
                }}
              >
                Retry
              </button>
              <p className="mx-auto">
                <a href="/home" className="underline">
                  Home
                </a>
              </p>
            </>
          ) : (
            <>
              <div className="mx-auto w-10 h-10 border-2 border-t-0 border-[#000] rounded-full animate-spin"></div>
              <p className="mx-auto">Setting up your legal space.</p>
            </>
          )}
          {/* </div> */}
        </div>
      )}
    </>
  );
};

export default PostAuth;
